/**
 * MO-02.1 interstitial scheduler: pure rules over explicit state and time.
 * Interstitials only after level `minLevel` (the round just played must be at
 * least that level, matching the API's daily audit), at most one every
 * `everyRounds` rounds and `minGapSec` apart, never after a fail, never within
 * `afterRewardedGapSec` of a rewarded ad, never for payers, and never while
 * remote config switches them off.
 */

/** Same shape as economy.ads.interstitial. */
export interface InterstitialRules {
  minLevel: number;
  everyRounds: number;
  minGapSec: number;
  afterRewardedGapSec: number;
  /** allow an interstitial right after a failed round (PRD: never) */
  afterFail: boolean;
}

export const DEFAULT_INTERSTITIAL_RULES: InterstitialRules = { minLevel: 15, everyRounds: 3, minGapSec: 180, afterRewardedGapSec: 90, afterFail: false };

/** Serializable; persist it with the player's local state. */
export interface InterstitialState {
  /** rounds finished since the last interstitial (or since install) */
  roundsSinceLast: number;
  lastShownAt: number | null;
  lastRewardedAt: number | null;
}

export const INITIAL_INTERSTITIAL_STATE: InterstitialState = { roundsSinceLast: 0, lastShownAt: null, lastRewardedAt: null };

export interface RoundEndContext {
  /** epoch ms */
  now: number;
  /** level number of the round that just ended (InterstitialLog.afterLevel) */
  level: number;
  result: 'win' | 'fail';
  /** any purchase ever (MetaState.payer) or Market Plus */
  payer: boolean;
  /** remote config: flags.ads && flags.interstitials (per country/platform/test group) */
  enabled: boolean;
}

export type InterstitialReason = 'ok' | 'disabled' | 'payer' | 'belowMinLevel' | 'afterFail' | 'recentRewarded' | 'roundGap' | 'timeGap';

export interface InterstitialDecision {
  show: boolean;
  reason: InterstitialReason;
}

const no = (reason: InterstitialReason): InterstitialDecision => ({ show: false, reason });

/** Decide for a round that `state` already counts. The first failing rule is the reason. */
export function decideInterstitial(rules: InterstitialRules, state: InterstitialState, ctx: RoundEndContext): InterstitialDecision {
  if (!ctx.enabled) return no('disabled');
  if (ctx.payer) return no('payer');
  if (ctx.level < rules.minLevel) return no('belowMinLevel');
  if (ctx.result === 'fail' && !rules.afterFail) return no('afterFail');
  if (state.lastRewardedAt !== null && ctx.now - state.lastRewardedAt < rules.afterRewardedGapSec * 1000) return no('recentRewarded');
  if (state.roundsSinceLast < rules.everyRounds) return no('roundGap');
  if (state.lastShownAt !== null && ctx.now - state.lastShownAt < rules.minGapSec * 1000) return no('timeGap');
  return { show: true, reason: 'ok' };
}

/** Stateful wrapper: count rounds, decide, and record what was actually shown. */
export class InterstitialScheduler {
  private s: InterstitialState;

  constructor(
    private readonly rules: InterstitialRules = DEFAULT_INTERSTITIAL_RULES,
    state: InterstitialState = INITIAL_INTERSTITIAL_STATE,
  ) {
    this.s = { ...state };
  }

  get state(): InterstitialState {
    return { ...this.s };
  }

  /**
   * Call once for every finished round (win or fail, any level). `rules` are the ones in force for this round
   * when they came from remote config after the scheduler was made (an A/B arm changes `minLevel`).
   */
  onRoundEnd(ctx: RoundEndContext, rules: InterstitialRules = this.rules): InterstitialDecision {
    this.s.roundsSinceLast += 1;
    return decideInterstitial(rules, this.s, ctx);
  }

  /** Call only when an interstitial was really displayed (a no-fill does not reset the gaps). */
  onInterstitialShown(now: number): void {
    this.s.roundsSinceLast = 0;
    this.s.lastShownAt = now;
  }

  /** Call when a rewarded ad was displayed (completed or not). */
  onRewardedShown(now: number): void {
    this.s.lastRewardedAt = now;
  }
}
